import React, { useCallback, useMemo, useRef, useState } from 'react'
import AsyncSelect, { useAsync } from 'react-select/async';
import { components, MultiValueGenericProps, MultiValueRemoveProps } from 'react-select';
import { useTags } from '../../../hooks/useTags';
import CloseIcon from '../../../public/images/icons/close.svg';
import SelectStyles from '../../Form/Select/SelectStyles';
import useTranslation from 'next-translate/useTranslation';

type TagOrderTuple = [{ 
    value: string;
    label: string;
}, number]

const MultiValueContainer = (props: MultiValueGenericProps<any>) => {
    return (
        <div className='rounded rounded-full px-2 py-1 text-white bg-primary mr-1 mb-1
      flex flex-row gap-x-1 cursor-pointer hover:bg-primary-focus' {...props}>
            {props.children}
        </div>
    );
};

const MultiValueLabel = (props: MultiValueGenericProps<any>) => {
    return <span className="font-bold text-[0.875rem]">{props.children}</span>;
};

const MultiValueRemove = (props: MultiValueRemoveProps<any>) => {
    return (
        <components.MultiValueRemove {...props}>
            <CloseIcon className="w-3 h-3 text-white" />
        </components.MultiValueRemove>
    );
};

export interface TagSelectProps {
    tags: string[];
    setTags: (tags: string[]) => void;
}
export const TagSelect = ({ tags, setTags }: TagSelectProps) => {
    const tagsQuery = useTags();
    const [ menuOpen, setMenuOpen ] = useState(false);
    const [ value, setValue ] = useState([]);
    const { t } = useTranslation('create');

    const options = useMemo(() => {
        return tagsQuery.data?.map(tag => {
            return {
                value: tag.slug,
                label: tag.name
            }
        }) || [];
    }, [tagsQuery]);

    const resultLimit = 5;

    const onChange = (value: any) => {
        setTags(value.map((val: any) => val.value));
    }

    const onInputChange = (typedOption: string) => {
        setMenuOpen(typedOption.trim().length !== 0);
    }

    return (
        <AsyncSelect
        value={options.filter(o => tags.some(tag => tag === o.value))}
        menuIsOpen={menuOpen}
        onInputChange={onInputChange}
        openMenuOnClick={false}
        openMenuOnFocus={false}
        defaultMenuIsOpen={false}
        menuShouldScrollIntoView={true}
        closeMenuOnSelect={false}
        onChange={onChange}
        isMulti
        placeholder={t('tags.placeholder')}
        noOptionsMessage={(args) => {
            if (args.inputValue.length === 0) {
                return t('tags.suggest');
            }
            else {
                return t('tags.notFound');
            }
        }}
        components={{ 
            MultiValueContainer,
            MultiValueLabel,
            MultiValueRemove,
            DropdownIndicator:() => null, 
            IndicatorSeparator:() => null }}
        loadOptions={(inputValue, callback) => {
            const input = inputValue.toLowerCase().trim();
            const filteredTags = options.filter(tagOption => {
                const tagName = tagOption.label.toLowerCase().trim();

                if (tags.some((value) => value === tagOption.value)) {
                    return false;
                }

                return tagName.indexOf(input) >= 0 
            })
            .map(tagOption => {
                const tagName = tagOption.label.toLowerCase().trim();

                return [tagOption, tagName.indexOf(input)] as TagOrderTuple; 
            })
            .sort((a, b) => {
                return (a[1] as number) - (b[1] as number);
            });

            const limitedTags = filteredTags.slice(0, resultLimit);

            callback(limitedTags.map(tag => tag[0]));
        }}
        options={options}
        styles={SelectStyles}
        theme={(theme) => ({
            ...theme,
            borderRadius: 16,
            colors: {
              ...theme.colors,
              primary: '#8c18f1',
              neutral0: 'white',
            },
          })}
      />
    )
}